// src/components/PaymentCancel/index.js - ENHANCED WITH REASON DISPLAY
import React, { useEffect, useState } from 'react';
import { XCircle, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './index.css';

const PaymentFailure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('validating');
  const [countdown, setCountdown] = useState(7);
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    const orderId = searchParams.get('order') || searchParams.get('order_id');
    const gateway = searchParams.get('gateway');
    const reason = searchParams.get('reason');

    if (orderId || gateway) {
      setOrderInfo({ orderId, gateway, reason });
    }

    const timer = setTimeout(() => setStatus('failed'), 1000);
    return () => clearTimeout(timer);
  }, [searchParams]);

  useEffect(() => {
    if (status === 'failed' && countdown > 0) {
      const t = setInterval(() => setCountdown(c => c - 1), 1000);
      return () => clearInterval(t);
    }

    if (status === 'failed' && countdown === 0) {
      handleRedirectToMenu();
    }
  }, [status, countdown]);

  const getStoreSlug = () => {
    try {
      const storeDetails = JSON.parse(sessionStorage.getItem('selected_store_details') || 'null');
      if (storeDetails?.name) {
        return storeDetails.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      }
    } catch (e) {
      // ignore
    }
    return null;
  };

  const handleRedirectToMenu = () => {
    const storeSlug = getStoreSlug();
    if (storeSlug) {
      navigate(`/restaurant/${storeSlug}`, { replace: true });
    } else {
      navigate('/restaurants', { replace: true });
    }
  };

  const handleTryAgainPayment = () => {
    const storeSlug = getStoreSlug();

    if (!storeSlug) {
      alert('Session expired. Please create a new order.');
      navigate('/restaurants', { replace: true });
      return;
    }

    // Check cart exists
    try {
      const storeDetails = JSON.parse(sessionStorage.getItem('selected_store_details') || 'null');
      const cartKey = `resto_cart_data_${storeDetails?.id}_delivery`;
      const savedCart = localStorage.getItem(cartKey);
      if (!savedCart) {
        // Try collection key
        const cartKeyCollection = `resto_cart_data_${storeDetails?.id}_collection`;
        const savedCartCollection = localStorage.getItem(cartKeyCollection);
        if (!savedCartCollection) {
          alert('Your cart is empty. The order may have timed out. Please create a new order.');
          navigate('/restaurants', { replace: true });
          return;
        }
      }
    } catch (e) {
      // proceed anyway
    }

    navigate(`/restaurant/${storeSlug}`, {
      replace: true,
      state: {
        openPaymentModal: true,
        fromPaymentFailure: true
      }
    });
  };

  const getGatewayName = () => {
    if (!orderInfo?.gateway) return null;
    const gateway = orderInfo.gateway.toLowerCase();

    switch (gateway) {
      case 'cashfree': return 'Cashfree';
      case 'razorpay': return 'Razorpay';
      case 'stripe': return 'Stripe';
      default: return gateway.charAt(0).toUpperCase() + gateway.slice(1);
    }
  };

  if (status === 'validating') {
    return (
      <div className="pp-container">
        <div className="pp-loader"></div>
        <p className="pp-subtext">Processing payment status…</p>
      </div>
    );
  }

  return (
    <div className="pp-container">
      <div className="pp-failure-circle">
        <XCircle size={48} color="#dc2626" />
      </div>
      <h2 className="pp-title-failure">Payment Failed</h2>

      {orderInfo?.orderId && (
        <div className="pp-order-id-failure">
          <span className="pp-order-id-label-failure">Order Reference:</span>
          <span className="pp-order-id-value-failure">#{orderInfo.orderId}</span>
        </div>
      )}

      {orderInfo?.gateway && (
        <p className="pp-gateway-info">
          Payment gateway: {getGatewayName()}
        </p>
      )}

      <p className="pp-subtext">
        {orderInfo?.reason === 'dismissed'
          ? 'You closed the payment window before completing the transaction. No charges were made.'
          : orderInfo?.reason
            ? `Payment could not be completed: ${decodeURIComponent(orderInfo.reason)}. No charges were made to your account.`
            : 'Your payment could not be processed. No charges were made to your account.'
        }
        {' '}Your cart is still available — you can try again.
      </p>

      <div className="pp-action-buttons">
        <button
          className="pp-try-again-btn"
          onClick={handleTryAgainPayment}
        >
          <ArrowLeft size={18} />
          Try Again
        </button>
      </div>

      <div className="pp-redirect">
        Returning to menu in {countdown}s
      </div>

      <p className="pp-help-text">
        Need help? Contact our support team
      </p>
    </div>
  );
};

export default PaymentFailure;